import SystemAdminLayout from '@/layouts/SystemAdminLayout'

interface TrendPoint { date: string; count: number }

interface DashboardProps {
  metrics: {
    accounts_total: number
    accounts_by_status: Record<string, number>
    accounts_paying: number
    accounts_comped: number
    trials_expiring_soon: number
    users_total: number
    surveys_total: number
    surveys_published: number
    responses_total: number
  }
  paying_breakdown: { currency: string; processor_plan: string; count: number }[]
  trends: { accounts: TrendPoint[]; users: TrendPoint[]; responses: TrendPoint[] }
  trend_days: number
}

const STATUS_BADGE: Record<string, string> = {
  trialing: 'badge-info',
  active: 'badge-success',
  past_due: 'badge-warning',
  canceled: 'badge-error',
  expired: 'badge-error',
}

// Dependency-free sparkline: one thin bar per day.
function Sparkline({ points }: { points: TrendPoint[] }) {
  const max = Math.max(...points.map((p) => p.count), 1)
  return (
    <div className="flex items-end gap-px h-10" aria-hidden>
      {points.map((p) => (
        <div
          key={p.date}
          className="flex-1 bg-primary/60 rounded-t-xs min-h-px"
          style={{ height: `${Math.max((p.count / max) * 100, p.count > 0 ? 8 : 2)}%` }}
          title={`${p.date}: ${p.count}`}
        />
      ))}
    </div>
  )
}

export default function Dashboard({ metrics, paying_breakdown, trends, trend_days }: DashboardProps) {
  const trendTotal = (points: TrendPoint[]) => points.reduce((a, p) => a + p.count, 0)

  return (
    <SystemAdminLayout>
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-base-content/60">Operational overview</p>
      </div>

      {/* Headline stats */}
      <div className="stats stats-vertical sm:stats-horizontal shadow bg-base-100 w-full">
        <div className="stat">
          <div className="stat-title">Organizations</div>
          <div className="stat-value">{metrics.accounts_total}</div>
          <div className="stat-desc">{metrics.accounts_paying} paying · {metrics.accounts_comped} comped</div>
        </div>
        <div className="stat">
          <div className="stat-title">Users</div>
          <div className="stat-value">{metrics.users_total}</div>
        </div>
        <div className="stat">
          <div className="stat-title">Surveys</div>
          <div className="stat-value">{metrics.surveys_total}</div>
          <div className="stat-desc">{metrics.surveys_published} published</div>
        </div>
        <div className="stat">
          <div className="stat-title">Responses</div>
          <div className="stat-value">{metrics.responses_total}</div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Plan states */}
        <div className="card bg-base-100">
          <div className="card-body space-y-3">
            <h2 className="card-title text-base">Plan states</h2>
            <table className="table table-sm">
              <tbody>
                {Object.entries(metrics.accounts_by_status).map(([status, count]) => (
                  <tr key={status}>
                    <td><span className={`badge badge-soft badge-sm ${STATUS_BADGE[status] ?? ''}`}>{status}</span></td>
                    <td className="text-end tabular-nums">{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {metrics.trials_expiring_soon > 0 && (
              <div className="alert alert-soft alert-info text-sm">
                <span className="iconify lucide--clock size-4" />
                {metrics.trials_expiring_soon} trial{metrics.trials_expiring_soon === 1 ? '' : 's'} expiring within 7 days
              </div>
            )}
          </div>
        </div>

        {/* Paying breakdown */}
        <div className="card bg-base-100">
          <div className="card-body space-y-3">
            <h2 className="card-title text-base">Active subscriptions</h2>
            {paying_breakdown.length === 0 ? (
              <p className="text-sm text-base-content/50">No active Stripe subscriptions yet.</p>
            ) : (
              <table className="table table-sm">
                <thead>
                  <tr><th>Currency</th><th>Price</th><th className="text-end">Orgs</th></tr>
                </thead>
                <tbody>
                  {paying_breakdown.map((row, i) => (
                    <tr key={i}>
                      <td>{row.currency}</td>
                      <td className="font-mono text-xs">{row.processor_plan}</td>
                      <td className="text-end tabular-nums">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Trends */}
      <div className="grid gap-6 lg:grid-cols-3">
        {([['New organizations', trends.accounts], ['New users', trends.users], ['Responses', trends.responses]] as const).map(([label, points]) => (
          <div key={label} className="card bg-base-100">
            <div className="card-body space-y-2">
              <div className="flex items-baseline justify-between">
                <h2 className="card-title text-sm">{label}</h2>
                <span className="text-xs text-base-content/50">{trendTotal(points)} in {trend_days}d</span>
              </div>
              <Sparkline points={points} />
            </div>
          </div>
        ))}
      </div>
    </SystemAdminLayout>
  )
}
